import { FC, useState } from 'react';

import {
  ARROW_DIRECTION_DOWN,
  ARROW_DIRECTION_UP,
  CollapsibleContent,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import {
  CollapsibleContentWrapper,
  FooterWrapper,
  UpperPanel,
  PrimaryPanel,
  WorkflowStatus,
  PanelWrapper,
  StyledSpinner,
} from '../styled';
import { BaseCardType } from '../NodeAnalyticsCard';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';

export const getCardTestIds = (cardType: BaseCardType) => ({
  CONTAINER: `${cardType}-card-container`,
  CONTENT: `${cardType}-card-content`,
  FOOTER: `${cardType}-card-footer`,
  TITLE: `${cardType}-card-title`,
  TOGGLE_BUTTON: `${cardType}-card-toggle-button`,
  LOADING_SPINNER: `${cardType}-card-loading-spinner`,
});

interface AnalyticsCardProps {
  /** The text/component in the non-collapsible section */
  title?: JSX.Element | React.ReactNode | string;
  /** The text/component in the collapsible section */
  content?: JSX.Element | React.ReactNode | string;
  /** The footer text/component, in the collapsible section */
  footer?: JSX.Element | React.ReactNode | string;
  /** is content loading */
  isLoading?: boolean;
  /** The content to be displayed when the card is selected */
  selectedTileContent?: JSX.Element | React.ReactNode;
  /** Should the card be collapsible */
  isCollapsible?: boolean;
  /** The type of card */
  cardType: BaseCardType;
}

/**
 * Card displaying analytics for a workflow or workflow tile.
 */
export const AnalyticsCard: FC<AnalyticsCardProps> = ({
  isLoading = false,
  content,
  footer,
  title,
  selectedTileContent,
  isCollapsible = true,
  cardType,
  ...props
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const testIds = getCardTestIds(cardType);
  return (
    <PanelWrapper data-iscollapsed={isCollapsed} data-test={testIds.CONTAINER} {...props}>
      <PrimaryPanel>
        <UpperPanel data-test={testIds.TITLE}>
          <WorkflowStatus>{title}</WorkflowStatus>
          {isCollapsible && (
            <IconButton
              data-test={testIds.TOGGLE_BUTTON}
              onClick={() => setIsCollapsed(!isCollapsed)}
              variant={BUTTON_VARIANTS.GHOST}
              size={BUTTON_SIZES.SMALL}
            >
              <CollapsibleContentArrow
                isCollapsed={isCollapsed}
                width={16}
                height={16}
                arrowEnd={ARROW_DIRECTION_UP}
                arrowStart={ARROW_DIRECTION_DOWN}
              />
            </IconButton>
          )}
        </UpperPanel>
        <CollapsibleContent data-test={testIds.CONTENT} isCollapsed={isCollapsed}>
          <CollapsibleContentWrapper>
            {isLoading ? <StyledSpinner data-test={testIds.LOADING_SPINNER} /> : content}
            {footer && <FooterWrapper data-test={testIds.FOOTER}>{footer}</FooterWrapper>}
          </CollapsibleContentWrapper>
        </CollapsibleContent>
      </PrimaryPanel>
      {selectedTileContent && selectedTileContent}
    </PanelWrapper>
  );
};
